import { FormControl, Grid, MenuItem, Select, TextField, Typography, Button, Box } from '@mui/material'
import { countriesList, industryOptions } from 'common/countries'
import React, { useEffect, useState } from 'react'
import { fetchBusinessData, updateBusinessData } from 'services/firebaseServices'
import { getProfile } from 'utilities'

const SettingBasicComp = () => {
    const [businessData, setBusinessData] = useState({
        companyName: '',
        industry: '',
        website: '',
        description: '',
        businessNumber: '',
        vatNumber: '',
        billingEmail: '',
        contactName: '',
        contactPhone: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        country: '',
        postalCode: ''
    })
    const [businessId, setBusinessId] = useState(" ");

    useEffect(() => {
        const loadBusinessData = async () => {
            const profile = getProfile()
            if (!profile?.uid) return

            const data = await fetchBusinessData(profile.uid)
            if (data && data.length > 0) {
                setBusinessData({
                    companyName: data[0].companyName || '',
                    industry: data[0].industry || '',
                    website: data[0].website || '',
                    description: data[0].description || '',
                    businessNumber: data[0].businessNumber || '',
                    vatNumber: data[0].vatNumber || '',
                    billingEmail: data[0].billingEmail || '',
                    contactName: data[0].contactName || '',
                    contactPhone: data[0].contactPhone || '',
                    addressLine1: data[0].addressLine1 || '',
                    addressLine2: data[0].addressLine2 || '',
                    city: data[0].city || '',
                    state: data[0].state || '',
                    country: data[0].country || '',
                    postalCode: data[0].postalCode || ''
                })
                setBusinessId(data[0].id)
            }
        }

        loadBusinessData()
    }, [])

    // Generic change handler for TextField and Select
    const handleChange = (e) => {
        const { name, value } = e.target;

        setBusinessData((prev) => ({
            ...prev,
            [name]: value
        }))
    }

    const handleCancel = () => {
    }

    const handleSave = async () => {
        const result = await updateBusinessData(businessId, businessData);
        if (result === true) {
            console.log('Business updated successfully.')
        } else {
            console.error('Failed to update business:', result.error)
        }
    }

    return (
        <Box
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    backgroundColor: '#FCFCFD',
                    transition: 'all .15s ease',
                    '& fieldset': { borderColor: '#E4E7EC' },
                    '&:hover fieldset': { borderColor: '#CDE9FB' },
                    '&.Mui-focused': { backgroundColor: '#fff' },
                    '&.Mui-focused fieldset': { borderColor: '#32ABF8', borderWidth: '1.5px' },
                },
                '& .MuiOutlinedInput-input': { padding: '12px 14px', fontSize: 14, color: '#101828' },
                '& .MuiSelect-select': { padding: '12px 14px', fontSize: 14, color: '#101828' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#32ABF8' },
                '& .MuiTypography-subtitle1': { fontSize: 13.5, fontWeight: 500, color: '#475467' },
            }}
        >
            <Typography fontWeight="bold" variant="h6" sx={{ pt: '24px', pb: '20px'}}>
                Company Details
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ pb: '8px' }}>Company Name</Typography>
                    <TextField
                        name="companyName"
                        variant="outlined"
                        fullWidth
                        value={businessData.companyName}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid item xs={12} md={6} sx={{ pl: { md: '20px !important' } }}>
                    <Typography variant="subtitle1" sx={{ pb: '8px' }}>Industry</Typography>
                    <FormControl fullWidth>
                        <Select
                            name="industry"
                            value={businessData.industry}
                            onChange={handleChange}
                        >
                            {industryOptions.map(({ value, label }, index) => (
                                <MenuItem key={index} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ pb: '8px' }}>Website</Typography>
                    <TextField
                        name="website"
                        variant="outlined"
                        fullWidth
                        value={businessData.website}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid item xs={12} md={6} sx={{ pl: { md: '20px !important' } }}>
                    <Typography variant="subtitle1" sx={{ pb: '8px' }}>Description</Typography>
                    <TextField
                        name="description"
                        variant="outlined"
                        fullWidth
                        value={businessData.description}
                        onChange={handleChange}
                    />
                </Grid>
            </Grid>

            <Typography fontWeight="bold" variant="h6" sx={{ pt: '32px', pb: '20px' }}>
                Billing &amp; Legal
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ pb: '8px' }}>Business Registration Number</Typography>
                    <TextField
                        name="businessNumber"
                        variant="outlined"
                        fullWidth
                        placeholder="e.g. CVR, Companies House No."
                        value={businessData.businessNumber}
                        onChange={handleChange}
                    />
                </Grid>
                <Grid item xs={12} md={6} sx={{ pl: { md: '20px !important' } }}>
                    <Typography variant="subtitle1" sx={{ pb: '8px' }}>VAT Number</Typography>
                    <TextField
                        name="vatNumber"
                        variant="outlined"
                        fullWidth
                        placeholder="e.g. DK12345678"
                        value={businessData.vatNumber}
                        onChange={handleChange}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ pb: '8px' }}>Billing Email</Typography>
                    <TextField
                        name="billingEmail"
                        variant="outlined"
                        fullWidth
                        type="email"
                        placeholder="invoices@yourcompany.com"
                        value={businessData.billingEmail}
                        onChange={handleChange}
                    />
                </Grid>
                <Grid item xs={12} md={6} sx={{ pl: { md: '20px !important' } }}>
                    <Typography variant="subtitle1" sx={{ pb: '8px' }}>Contact Person</Typography>
                    <TextField
                        name="contactName"
                        variant="outlined"
                        fullWidth
                        placeholder="Full name"
                        value={businessData.contactName}
                        onChange={handleChange}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ pb: '8px' }}>Contact Phone</Typography>
                    <TextField
                        name="contactPhone"
                        variant="outlined"
                        fullWidth
                        placeholder="+45 12 34 56 78"
                        value={businessData.contactPhone}
                        onChange={handleChange}
                    />
                </Grid>
            </Grid>

            <Typography fontWeight="bold" variant="h6" sx={{ pt: '32px', pb: '20px' }}>
                Address Information
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ pb: '8px' }}>Address Line 1</Typography>
                    <TextField
                        name="addressLine1"
                        variant="outlined"
                        fullWidth
                        value={businessData.addressLine1}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid item xs={12} md={6} sx={{ pl: { md: '20px !important' } }}>
                    <Typography variant="subtitle1" sx={{ pb: '8px' }}>Address Line 2</Typography>
                    <TextField
                        name="addressLine2"
                        variant="outlined"
                        fullWidth
                        value={businessData.addressLine2}
                        onChange={handleChange}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ pb: '8px' }}>City</Typography>
                    <TextField
                        name="city"
                        variant="outlined"
                        fullWidth
                        value={businessData.city}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid item xs={12} md={6} sx={{ pl: { md: '20px !important' } }}>
                    <Typography variant="subtitle1" sx={{ pb: '8px' }}>State / Province</Typography>
                    <TextField
                        name="state"
                        variant="outlined"
                        fullWidth
                        value={businessData.state}
                        onChange={handleChange}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ pb: '8px' }}>Country</Typography>


                    <FormControl fullWidth>
                        <Select
                            name="country"
                            value={businessData.country}
                            onChange={handleChange}
                        >
                            {countriesList.map(({ value, label }, index) => (
                                <MenuItem key={index} value={value}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6} sx={{ pl: { md: '20px !important' } }}>
                    <Typography variant="subtitle1" sx={{ pb: '8px' }}>Postal Code</Typography>
                    <TextField
                        name="postalCode"
                        variant="outlined"
                        fullWidth
                        value={businessData.postalCode}
                        onChange={handleChange}
                    />
                </Grid>
            </Grid>

            {/* Submit Button */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                mt: { xs: '20px', md: '110px' }, // 20px on small screens, 110px on md and above
                mb: '20px',
            }}>
                <Button variant="outlined"
                    sx={{
                        color: 'black', borderColor: 'grey.400', fontFamily: 'Inter',
                        fontWeight: 500,
                        fontSize: '14px',
                        textTransform: 'none',
                    }}
                    onClick={handleCancel}
                >
                    Cancel
                </Button>
                <Button sx={{
                    fontFamily: 'Inter',
                    fontWeight: 500,
                    fontSize: '14px',
                    textTransform: 'none',
                }}
                    variant="contained" color="primary"
                    onClick={handleSave}
                >
                    Save Changes
                </Button>
            </Box>
        </Box>
    )
}

export default SettingBasicComp
